// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * "Bygningsdele" filter state: the web counterpart of the Qt client's
 * `rux_qt/element_filter.hpp` (ReUseX #98) — which categories (walls, slabs,
 * roof, spaces, doors, windows, other openings) and which storey are shown
 * in the 3D viewer, with the counts its rows show. `buildingElements.ts`
 * turns the API's walls/openings/spaces/meshes into the `ElementTally[]`
 * this module reads; this module itself is standard TS, no React, no DOM —
 * vitest covers it directly (frontend-t02).
 */

/** How many elements of one category one storey holds; `storeyId` null = no storey. */
export interface ElementTally {
  category: string;
  storeyId: number | null;
  count: number;
}

/** One row of the panel: a category the project has, under the current storey filter. */
export interface ElementCategoryRow {
  key: string;
  label: string;
  count: number;
}

export type ElementCheckState = 'on' | 'off' | 'partial';

/** "All storeys", one storey, or one storey and every one under it. */
export interface StoreyFilterState {
  storeyId: number | null;
  andBelow: boolean;
}

export interface ElementFilterState {
  hidden: ReadonlySet<string>;
  storey: StoreyFilterState;
}

export const INITIAL_STOREY_FILTER: StoreyFilterState = { storeyId: null, andBelow: false };
export const INITIAL_ELEMENT_FILTER: ElementFilterState = { hidden: new Set(), storey: INITIAL_STOREY_FILTER };

/** `kElementCategories`' order and Danish labels, from `rux_qt/element_filter.cpp`. */
const CATEGORY_LABELS: ReadonlyMap<string, string> = new Map([
  ['wall', 'Vægge'],
  ['slab', 'Dæk'],
  ['roof', 'Tag'],
  ['space', 'Rum'],
  ['door', 'Døre'],
  ['window', 'Vinduer'],
  ['opening', 'Andre åbninger'],
]);

const CATEGORY_ORDER: readonly string[] = [...CATEGORY_LABELS.keys()];

/** The Danish row label of category `key` ("wall" → "Vægge"); an unknown key is returned as it is. */
export function elementCategoryLabel(key: string): string {
  return CATEGORY_LABELS.get(key) ?? key;
}

/** Whether `storeyId` matches the storey filter; an element with no storey (`null`) is never filtered out. */
export function storeyMatches(filter: StoreyFilterState, storeyId: number | null): boolean {
  if (filter.storeyId === null || storeyId === null) return true;
  return filter.andBelow ? storeyId <= filter.storeyId : storeyId === filter.storeyId;
}

/** Total count per category, ignoring the storey filter (whether the category has anything at all). */
function categoryTotals(tallies: readonly ElementTally[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const t of tallies) totals.set(t.category, (totals.get(t.category) ?? 0) + t.count);
  return totals;
}

/**
 * The categories present (total count > 0), in `kElementCategories` order
 * then by first appearance for anything else, each counted under the
 * current storey filter (an element with no storey always counts).
 */
export function elementCategoryRows(tallies: readonly ElementTally[], state: Pick<ElementFilterState, 'storey'>): ElementCategoryRow[] {
  const totals = categoryTotals(tallies);
  const under = new Map<string, number>();
  for (const t of tallies) if (storeyMatches(state.storey, t.storeyId)) under.set(t.category, (under.get(t.category) ?? 0) + t.count);

  const rows: ElementCategoryRow[] = [];
  const seen = new Set<string>();
  const addIfPresent = (key: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    if ((totals.get(key) ?? 0) > 0) rows.push({ key, label: elementCategoryLabel(key), count: under.get(key) ?? 0 });
  };
  for (const key of CATEGORY_ORDER) addIfPresent(key);
  for (const t of tallies) addIfPresent(t.category);
  return rows;
}

/** The storeys (>= 0 by convention, but any value) that hold an element, ascending. */
export function elementFilterStoreys(tallies: readonly ElementTally[]): number[] {
  const s = new Set<number>();
  for (const t of tallies) if (t.storeyId !== null && t.count > 0) s.add(t.storeyId);
  return [...s].sort((a, b) => a - b);
}

export function isElementCategoryShown(state: Pick<ElementFilterState, 'hidden'>, category: string): boolean {
  return !state.hidden.has(category);
}

export function withElementCategoryShown(state: ElementFilterState, category: string, on: boolean): ElementFilterState {
  const hidden = new Set(state.hidden);
  if (on) hidden.delete(category);
  else hidden.add(category);
  return { ...state, hidden };
}

/** Over the categories present (under the storey filter's row list): on (all shown), off, or partial. */
export function elementAllState(tallies: readonly ElementTally[], state: ElementFilterState): ElementCheckState {
  const keys = elementCategoryRows(tallies, state).map((r) => r.key);
  if (keys.length === 0) return 'off';
  const shown = keys.filter((k) => isElementCategoryShown(state, k)).length;
  if (shown === 0) return 'off';
  return shown === keys.length ? 'on' : 'partial';
}

/** "Vis alle": show every present category, or hide every one of them. */
export function withAllElementsShown(tallies: readonly ElementTally[], state: ElementFilterState, on: boolean): ElementFilterState {
  if (on) return { ...state, hidden: new Set() };
  const keys = elementCategoryRows(tallies, state).map((r) => r.key);
  return { ...state, hidden: new Set(keys) };
}

export function withElementStorey(state: ElementFilterState, storey: StoreyFilterState): ElementFilterState {
  return { ...state, storey };
}

/** Whether an element of `category` (empty = untagged, never filtered) on `storeyId` is drawn. */
export function isElementVisible(state: ElementFilterState, category: string, storeyId: number | null): boolean {
  if (category !== '' && !isElementCategoryShown(state, category)) return false;
  return storeyMatches(state.storey, storeyId);
}

/** `ElementFilterState` as plain arrays/objects — what the persisted settings store (`settingsStorage.ts`, JSON). */
export interface SerializedElementFilter {
  hidden: string[];
  storey: StoreyFilterState;
}

export const INITIAL_SERIALIZED_ELEMENT_FILTER: SerializedElementFilter = { hidden: [], storey: INITIAL_STOREY_FILTER };

export function toElementFilterState(s: SerializedElementFilter): ElementFilterState {
  return { hidden: new Set(s.hidden), storey: s.storey };
}

export function fromElementFilterState(s: ElementFilterState): SerializedElementFilter {
  return { hidden: [...s.hidden], storey: s.storey };
}
